import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Snackbar,
} from '@mui/material';
import { Upload as UploadIcon } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import ArchiveIconSvg from '../components/common/ArchiveIconSvg';
import { publisherService, Publisher } from '../services/publisherService';
import { useAuth } from '../contexts/AuthContext';
import PublisherBulkUploadModal from '../components/publishers/PublisherBulkUploadModal';
import { AdminPageHeader } from '../components/common/AdminPageShell';
import { invalidateArchiveRestoreQueries } from '../utils/workflowPanelCache';

const PublishersPage: React.FC = () => {
  const { isSuperAdmin } = useAuth();
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [selectedPublisher, setSelectedPublisher] = useState<Publisher | undefined>();
  const [deleteError, setDeleteError] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterCommunication, setFilterCommunication] = useState<string>('');
  const [filterAccount, setFilterAccount] = useState<string>('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [formData, setFormData] = useState({
    publisherName: '',
    email: '',
    whatsapp: '',
    modeOfCommunication: 'EMAIL',
    isActive: true,
  });

  const queryClient = useQueryClient();

  const { data: publishersData, isLoading, error } = useQuery({
    queryKey: ['publishers', 'list', filterStatus],
    queryFn: () =>
      publisherService.getPublishers({
        limit: 1000,
        status: filterStatus === 'Inactive' ? 'INACTIVE' : 'ACTIVE',
      }),
  });

  const createMutation = useMutation({
    mutationFn: publisherService.createPublisher,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['publishers'] });
      setFormOpen(false);
      const publisherName = formData.publisherName;
      setFormData({
        publisherName: '',
        email: '',
        whatsapp: '',
        modeOfCommunication: 'EMAIL',
        isActive: true,
      });
      setSnackbar({ open: true, message: `A new publisher '${publisherName}' has been created.` });
    },
    onError: (error: any) => {
      setDeleteError(error.response?.data?.error || 'Failed to create publisher');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      publisherService.updatePublisher(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['publishers'] });
      setFormOpen(false);
      setSelectedPublisher(undefined);
    },
    onError: (error: any) => {
      setDeleteError(error.response?.data?.error || 'Failed to update publisher');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: publisherService.deletePublisher,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['publishers'] });
      invalidateArchiveRestoreQueries(queryClient);
      setDeleteError('');
      setSnackbar({ open: true, message: 'Publisher archived successfully.' });
    },
    onError: (error: any) => {
      setDeleteError(error.response?.data?.error || 'Failed to archive publisher');
    },
  });

  const publishers = Array.isArray(publishersData) ? publishersData : (publishersData?.data || []);

  const handleSubmit = async () => {
    if (!formData.publisherName.trim()) {
      setDeleteError('Publisher name is required');
      return;
    }
    
    if (!formData.email.trim()) {
      setDeleteError('Email is required');
      return;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      setDeleteError('Please enter a valid email address');
      return;
    }
    
    const submitData = {
      ...formData,
      email: formData.email.trim(),
      whatsapp: formData.whatsapp || undefined,
      modeOfCommunication: formData.modeOfCommunication as 'EMAIL' | 'WHATSAPP',
    };

    try {
      if (selectedPublisher) {
        await updateMutation.mutateAsync({ id: selectedPublisher.id, data: submitData });
      } else {
        await createMutation.mutateAsync(submitData);
      }
    } catch (error: any) {
      // Prevent unhandled promise rejection in UI; show backend validation message.
      setDeleteError(error?.response?.data?.error || 'Failed to save publisher');
    }
  };

  const handleEdit = (publisher: Publisher) => {
    setSelectedPublisher(publisher);
    setFormData({
      publisherName: publisher.publisherName,
      email: publisher.email || '',
      whatsapp: publisher.whatsapp || '',
      modeOfCommunication: publisher.modeOfCommunication,
      isActive: publisher.isActive,
    });
    setFormOpen(true);
  };

  const handleArchive = (publisher: Publisher) => {
    if (window.confirm(
      `Archive "${publisher.publisherName}"? It will be removed from this list and can be restored from Settings → Restore Archived Data.`
    )) {
      deleteMutation.mutate(publisher.id);
    }
  };

  const handleAddNew = () => {
    setSelectedPublisher(undefined);
    setFormData({
      publisherName: '',
      email: '',
      whatsapp: '',
      modeOfCommunication: 'EMAIL',
      isActive: true,
    });
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setSelectedPublisher(undefined);
    setDeleteError('');
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getColorClass = (index: number) => {
    const colors = ['c1', 'c2', 'c3', 'c4', 'c5', 'c6'];
    return colors[index % colors.length];
  };

  const filteredPublishers = publishers.filter((publisher: Publisher) => {
    const matchesSearch = !search || 
      publisher.publisherName.toLowerCase().includes(search.toLowerCase()) ||
      (publisher.email && publisher.email.toLowerCase().includes(search.toLowerCase()));
    const matchesStatus = !filterStatus ||
      (filterStatus === 'Active' && publisher.isActive) ||
      (filterStatus === 'Inactive' && !publisher.isActive);
    const matchesCommunication = !filterCommunication || publisher.modeOfCommunication === filterCommunication;
    const matchesAccount = !filterAccount ||
      (filterAccount === 'No Account' && !publisher.userId) ||
      (filterAccount === 'User Linked' && publisher.userId);
    
    return matchesSearch && matchesStatus && matchesCommunication && matchesAccount;
  });

  if (error) {
    return (
      <Box>
        <Alert severity="error">
          Failed to load publishers. Please try again.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ padding: '28px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <AdminPageHeader
        title="Publishers"
        subtitle="Manage your guest post publishers and outreach contacts"
        actions={
        <Box sx={{ display: 'flex', gap: '12px' }}>
          <Button
            onClick={() => setBulkUploadOpen(true)}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              borderRadius: 'var(--radius-sm)',
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer',
              border: '1px solid var(--border)',
              transition: 'all 0.15s',
              whiteSpace: 'nowrap',
              background: 'var(--surface)',
              color: 'var(--text-primary)',
              '&:hover': {
                background: 'var(--surface-hover)',
                borderColor: 'var(--accent)'
              }
            }}
          >
            <UploadIcon sx={{ width: '14px', height: '14px' }} />
            Bulk Upload
          </Button>
          <Button
            onClick={handleAddNew}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              borderRadius: 'var(--radius-sm)',
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer',
              border: 'none',
              transition: 'all 0.15s',
              whiteSpace: 'nowrap',
              background: 'var(--accent)',
              color: 'white',
              boxShadow: '0 1px 2px rgba(37,99,235,0.2)',
              '&:hover': {
                background: 'var(--accent-hover)',
                boxShadow: 'var(--shadow-md)'
              }
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px' }}>
              <line x1="12" y1="5" x2="12" y2="19"/>
              <line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            Add Publisher
          </Button>
        </Box>
        }
      />

      {/* Stats */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '14px' }}>
        <Box sx={{ 
          background: 'var(--surface)', 
          border: '1px solid var(--border)', 
          borderRadius: 'var(--radius)', 
          padding: '16px 18px', 
          boxShadow: 'var(--shadow)', 
          display: 'flex', 
          alignItems: 'center', 
          gap: '14px', 
          transition: 'all 0.2s',
          '&:hover': { boxShadow: 'var(--shadow-md)', transform: 'translateY(-1px)' }
        }}>
          <Box sx={{ 
            width: '36px', 
            height: '36px', 
            borderRadius: '9px', 
            background: 'var(--accent-soft)', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center' 
          }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '16px', height: '16px', color: 'var(--accent)' }}>
              <circle cx="12" cy="12" r="10"/>
              <line x1="2" y1="12" x2="22" y2="12"/>
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
            </svg>
          </Box>
          <Box>
            <Box sx={{ fontSize: '20px', fontWeight: 700, letterSpacing: '-0.5px', lineHeight: 1 }}>
              {filteredPublishers.length}
            </Box>
            <Box sx={{ fontSize: '11.5px', color: 'var(--text-muted)', mt: '2px' }}>
              Total Publishers
            </Box>
          </Box>
        </Box>
        
        <Box sx={{ 
          background: 'var(--surface)', 
          border: '1px solid var(--border)', 
          borderRadius: 'var(--radius)', 
          padding: '16px 18px', 
          boxShadow: 'var(--shadow)', 
          display: 'flex', 
          alignItems: 'center', 
          gap: '14px', 
          transition: 'all 0.2s',
          '&:hover': { boxShadow: 'var(--shadow-md)', transform: 'translateY(-1px)' }
        }}>
          <Box sx={{ 
            width: '36px', 
            height: '36px', 
            borderRadius: '9px', 
            background: 'var(--green-soft)', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center' 
          }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '16px', height: '16px', color: 'var(--green)' }}>
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </Box>
          <Box>
            <Box sx={{ fontSize: '20px', fontWeight: 700, letterSpacing: '-0.5px', lineHeight: 1 }}>
              {filteredPublishers.filter((p: Publisher) => p.isActive).length}
            </Box>
            <Box sx={{ fontSize: '11.5px', color: 'var(--text-muted)', mt: '2px' }}>
              Active
            </Box>
          </Box>
        </Box>
        
        <Box sx={{ 
          background: 'var(--surface)', 
          border: '1px solid var(--border)', 
          borderRadius: 'var(--radius)', 
          padding: '16px 18px', 
          boxShadow: 'var(--shadow)', 
          display: 'flex', 
          alignItems: 'center', 
          gap: '14px', 
          transition: 'all 0.2s',
          '&:hover': { boxShadow: 'var(--shadow-md)', transform: 'translateY(-1px)' }
        }}>
          <Box sx={{ 
            width: '36px', 
            height: '36px', 
            borderRadius: '9px', 
            background: 'var(--teal-soft)', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center' 
          }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '16px', height: '16px', color: 'var(--teal)' }}>
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
              <polyline points="22,6 12,13 2,6"/>
            </svg>
          </Box>
          <Box>
            <Box sx={{ fontSize: '20px', fontWeight: 700, letterSpacing: '-0.5px', lineHeight: 1 }}>
              {filteredPublishers.filter((p: Publisher) => p.modeOfCommunication === 'EMAIL').length}
            </Box>
            <Box sx={{ fontSize: '11.5px', color: 'var(--text-muted)', mt: '2px' }}>
              Email Contacts
            </Box>
          </Box>
        </Box>
        
        <Box sx={{ 
          background: 'var(--surface)', 
          border: '1px solid var(--border)', 
          borderRadius: 'var(--radius)', 
          padding: '16px 18px', 
          boxShadow: 'var(--shadow)', 
          display: 'flex', 
          alignItems: 'center', 
          gap: '14px', 
          transition: 'all 0.2s',
          '&:hover': { boxShadow: 'var(--shadow-md)', transform: 'translateY(-1px)' }
        }}>
          <Box sx={{ 
            width: '36px', 
            height: '36px', 
            borderRadius: '9px', 
            background: 'var(--purple-soft)', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center' 
          }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '16px', height: '16px', color: 'var(--purple)' }}>
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
          </Box>
          <Box>
            <Box sx={{ fontSize: '20px', fontWeight: 700, letterSpacing: '-0.5px', lineHeight: 1 }}>
              {filteredPublishers.filter((p: Publisher) => p.modeOfCommunication === 'WHATSAPP').length}
            </Box>
            <Box sx={{ fontSize: '11.5px', color: 'var(--text-muted)', mt: '2px' }}>
              WhatsApp Contacts
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px', 
          background: 'var(--surface)', 
          border: '1px solid var(--border)', 
          borderRadius: 'var(--radius-sm)', 
          padding: '8px 14px', 
          flex: 1, 
          minWidth: '320px',
          maxWidth: '480px', 
          transition: 'all 0.15s', 
          boxShadow: 'var(--shadow)',
          '&:focus-within': { 
            borderColor: 'var(--accent)', 
            boxShadow: '0 0 0 3px rgba(37,99,235,0.08)' 
          } 
        }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px', color: 'var(--text-muted)', flexShrink: 0 }}>
            <circle cx="11" cy="11" r="8"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <TextField
            placeholder="Search publishers by name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            variant="standard"
            InputProps={{
              disableUnderline: true,
              style: { fontSize: '13px', color: 'var(--text-primary)', width: '100%' }
            }}
          />
        </Box>
        
        <FormControl sx={{ minWidth: '150px' }}>
          <Select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            displayEmpty
            sx={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              padding: '8px 32px 8px 12px',
              fontSize: '13px',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              outline: 'none',
              boxShadow: 'var(--shadow)',
              '& .MuiSelect-select': {
                padding: 0,
                background: 'none',
                border: 'none'
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none'
              },
            }}
          >
            <MenuItem value="">All Status</MenuItem>
            <MenuItem value="Active">Active</MenuItem>
            <MenuItem value="Inactive">Inactive (paused)</MenuItem>
          </Select>
        </FormControl>
        
        <FormControl sx={{ minWidth: '150px' }}>
          <Select
            value={filterCommunication}
            onChange={(e) => setFilterCommunication(e.target.value)}
            displayEmpty
            sx={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              padding: '8px 32px 8px 12px',
              fontSize: '13px',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              outline: 'none',
              boxShadow: 'var(--shadow)',
              '& .MuiSelect-select': {
                padding: 0,
                background: 'none',
                border: 'none'
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none'
              },
            }}
          >
            <MenuItem value="">Mode of Communication</MenuItem>
            <MenuItem value="EMAIL">Email</MenuItem>
            <MenuItem value="WHATSAPP">WhatsApp</MenuItem>
          </Select>
        </FormControl>
        
        <FormControl sx={{ minWidth: '150px' }}>
          <Select
            value={filterAccount}
            onChange={(e) => setFilterAccount(e.target.value)}
            displayEmpty
            sx={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              padding: '8px 32px 8px 12px',
              fontSize: '13px',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              outline: 'none',
              boxShadow: 'var(--shadow)',
              '& .MuiSelect-select': {
                padding: 0,
                background: 'none',
                border: 'none'
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none'
              },
            }}
          >
            <MenuItem value="">All Accounts</MenuItem>
            <MenuItem value="No Account">No Account</MenuItem>
            <MenuItem value="User Linked">User Linked</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Table */}
      <Box sx={{ 
        background: 'var(--surface)', 
        border: '1px solid var(--border)', 
        borderRadius: 'var(--radius)', 
        boxShadow: 'var(--shadow)', 
        overflow: 'hidden',
        animation: 'fadeUp 0.35s ease forwards'
      }}>
        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
          <Box component="thead">
            <Box component="tr">
              <Box component="th" sx={{ 
                textAlign: 'left', 
                fontSize: '11px', 
                fontWeight: 600, 
                color: 'var(--text-muted)', 
                textTransform: 'uppercase', 
                letterSpacing: '0.6px', 
                padding: '12px 18px', 
                borderBottom: '1px solid var(--border-light)', 
                background: 'var(--bg)', 
                whiteSpace: 'nowrap' 
              }}>
                Publisher Name
              </Box>
              <Box component="th" sx={{ 
                textAlign: 'left', 
                fontSize: '11px', 
                fontWeight: 600, 
                color: 'var(--text-muted)', 
                textTransform: 'uppercase', 
                letterSpacing: '0.6px', 
                padding: '12px 18px', 
                borderBottom: '1px solid var(--border-light)', 
                background: 'var(--bg)', 
                whiteSpace: 'nowrap' 
              }}>
                Email
              </Box>
              <Box component="th" sx={{ 
                textAlign: 'left', 
                fontSize: '11px', 
                fontWeight: 600, 
                color: 'var(--text-muted)', 
                textTransform: 'uppercase', 
                letterSpacing: '0.6px', 
                padding: '12px 18px', 
                borderBottom: '1px solid var(--border-light)', 
                background: 'var(--bg)', 
                whiteSpace: 'nowrap' 
              }}>
                User Account
              </Box>
              <Box component="th" sx={{ 
                textAlign: 'left', 
                fontSize: '11px', 
                fontWeight: 600, 
                color: 'var(--text-muted)', 
                textTransform: 'uppercase', 
                letterSpacing: '0.6px', 
                padding: '12px 18px', 
                borderBottom: '1px solid var(--border-light)', 
                background: 'var(--bg)', 
                whiteSpace: 'nowrap' 
              }}>
                Communication
              </Box>
              <Box component="th" sx={{ 
                textAlign: 'left', 
                fontSize: '11px', 
                fontWeight: 600, 
                color: 'var(--text-muted)', 
                textTransform: 'uppercase', 
                letterSpacing: '0.6px', 
                padding: '12px 18px', 
                borderBottom: '1px solid var(--border-light)', 
                background: 'var(--bg)', 
                whiteSpace: 'nowrap' 
              }}>
                Status
              </Box>
              <Box component="th" sx={{ 
                textAlign: 'left', 
                fontSize: '11px', 
                fontWeight: 600, 
                color: 'var(--text-muted)', 
                textTransform: 'uppercase', 
                letterSpacing: '0.6px', 
                padding: '12px 18px', 
                borderBottom: '1px solid var(--border-light)', 
                background: 'var(--bg)', 
                whiteSpace: 'nowrap' 
              }}>
                Created
              </Box>
              <Box component="th" sx={{ 
                textAlign: 'left', 
                fontSize: '11px', 
                fontWeight: 600, 
                color: 'var(--text-muted)', 
                textTransform: 'uppercase', 
                letterSpacing: '0.6px', 
                padding: '12px 18px', 
                borderBottom: '1px solid var(--border-light)', 
                background: 'var(--bg)', 
                whiteSpace: 'nowrap' 
              }}>
                Actions
              </Box>
            </Box>
          </Box>
          <Box component="tbody">
            {isLoading ? (
              <Box component="tr">
                <Box component="td" colSpan={7} align="center" sx={{ padding: '40px 18px', fontSize: '13px', color: 'var(--text-muted)' }}>
                  Loading publishers...
                </Box>
              </Box>
            ) : filteredPublishers.length === 0 ? (
              <Box component="tr">
                <Box component="td" colSpan={7} align="center" sx={{ padding: '40px 18px', fontSize: '13px', color: 'var(--text-muted)' }}>
                  No publishers found
                </Box>
              </Box>
            ) : (
              filteredPublishers
                .slice(page * rowsPerPage, (page + 1) * rowsPerPage)
                .map((publisher: Publisher, index: number) => (
                <Box component="tr" key={publisher.id} sx={{ 
                  transition: 'background 0.1s',
                  '&:hover': { background: '#fafbff' }
                }}>
                  <Box component="td" sx={{ padding: '13px 18px', fontSize: '13px', borderBottom: '1px solid var(--border-light)', verticalAlign: 'middle' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <Box sx={{ 
                        width: '34px', 
                        height: '34px', 
                        borderRadius: '9px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        fontSize: '12px', 
                        fontWeight: 700, 
                        color: 'white', 
                        flexShrink: 0, 
                        letterSpacing: '0.3px',
                        background: getColorClass(index) === 'c1' ? 'linear-gradient(135deg,#3b82f6,#2563eb)' :
                                      getColorClass(index) === 'c2' ? 'linear-gradient(135deg,#22c55e,#16a34a)' :
                                      getColorClass(index) === 'c3' ? 'linear-gradient(135deg,#a78bfa,#7c3aed)' :
                                      getColorClass(index) === 'c4' ? 'linear-gradient(135deg,#fbbf24,#d97706)' :
                                      getColorClass(index) === 'c5' ? 'linear-gradient(135deg,#fb7185,#e11d48)' :
                                      'linear-gradient(135deg,#22d3ee,#0891b2)'
                      }}>
                        {getInitials(publisher.publisherName)}
                      </Box>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                          <Box component="span" sx={{ fontWeight: 500, color: 'var(--text-primary)' }}>
                            {publisher.publisherName}
                          </Box>
                          <Box
                            component="span"
                            sx={{
                              fontSize: '11.5px',
                              fontWeight: 500,
                              color: (publisher.siteCount || 0) > 0 ? 'var(--text-secondary)' : 'var(--text-muted)',
                            }}
                          >
                            ({publisher.siteCount ?? 0}{' '}
                            {(publisher.siteCount ?? 0) === 1 ? 'site' : 'sites'})
                          </Box>
                        </Box>
                        {publisher.whatsapp && (
                          <Box sx={{ fontSize: '11.5px', color: 'var(--text-muted)', mt: '1px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: '10px', height: '10px', color: '#25d366' }}>
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                            </svg>
                            {publisher.whatsapp}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>
                  <Box component="td" sx={{ padding: '13px 18px', fontSize: '13px', borderBottom: '1px solid var(--border-light)', verticalAlign: 'middle' }}>
                    {publisher.email ? (
                      <Box sx={{ color: 'var(--text-secondary)', fontSize: '12.5px', fontFamily: '"DM Mono", monospace' }}>
                        {publisher.email}
                      </Box>
                    ) : (
                      <Box sx={{ color: 'var(--text-muted)', fontSize: '12.5px', fontFamily: '"DM Mono", monospace', fontStyle: 'italic' }}>
                        —
                      </Box>
                    )}
                  </Box>
                  <Box component="td" sx={{ padding: '13px 18px', fontSize: '13px', borderBottom: '1px solid var(--border-light)', verticalAlign: 'middle' }}>
                    <Box sx={{ 
                      display: 'inline-flex', 
                      alignItems: 'center', 
                      gap: '5px', 
                      padding: '3px 9px', 
                      borderRadius: '99px', 
                      fontSize: '11.5px', 
                      fontWeight: 500,
                      background: publisher.userId ? 'var(--green-soft)' : 'var(--surface2)',
                      color: publisher.userId ? 'var(--green)' : 'var(--text-muted)',
                      border: publisher.userId ? '1px solid var(--green-border)' : '1px solid var(--border)'
                    }}>
                      {publisher.userId ? 'User Linked' : 'No Account'}
                    </Box>
                  </Box>
                  <Box component="td" sx={{ padding: '13px 18px', fontSize: '13px', borderBottom: '1px solid var(--border-light)', verticalAlign: 'middle' }}>
                    <Box sx={{ 
                      display: 'inline-flex', 
                      alignItems: 'center', 
                      gap: '5px', 
                      padding: '3px 9px', 
                      borderRadius: '99px', 
                      fontSize: '11.5px', 
                      fontWeight: 500,
                      background: publisher.modeOfCommunication === 'EMAIL' ? 'var(--accent-soft)' : '#f0fdf4',
                      color: publisher.modeOfCommunication === 'EMAIL' ? 'var(--accent)' : '#16a34a',
                      border: publisher.modeOfCommunication === 'EMAIL' ? '1px solid #c7d7fd' : '1px solid #bbf7d0'
                    }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '11px', height: '11px' }}>
                        {publisher.modeOfCommunication === 'EMAIL' ? (
                          <>
                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                            <polyline points="22,6 12,13 2,6"/>
                          </>
                        ) : (
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                        )}
                      </svg>
                      {publisher.modeOfCommunication === 'EMAIL' ? 'Email' : 'WhatsApp'}
                    </Box>
                  </Box>
                  <Box component="td" sx={{ padding: '13px 18px', fontSize: '13px', borderBottom: '1px solid var(--border-light)', verticalAlign: 'middle' }}>
                    <Box sx={{ 
                      display: 'inline-flex', 
                      alignItems: 'center', 
                      gap: '5px', 
                      padding: '3px 9px', 
                      borderRadius: '99px', 
                      fontSize: '11.5px', 
                      fontWeight: 600,
                      background: publisher.isActive ? 'var(--green-soft)' : 'var(--surface2)',
                      color: publisher.isActive ? 'var(--green)' : 'var(--text-muted)',
                      border: publisher.isActive ? '1px solid var(--green-border)' : '1px solid var(--border)',
                      '&::before': {
                        content: '""',
                        width: '5px',
                        height: '5px',
                        borderRadius: '50%',
                        background: publisher.isActive ? 'var(--green)' : 'var(--text-muted)',
                        marginRight: '2px'
                      }
                    }}>
                      {publisher.isActive ? 'Active' : 'Inactive'}
                    </Box>
                  </Box>
                  <Box component="td" sx={{ padding: '13px 18px', fontSize: '13px', borderBottom: '1px solid var(--border-light)', verticalAlign: 'middle' }}>
                    <Box sx={{ fontSize: '12px', color: 'var(--text-muted)', fontFamily: '"DM Mono", monospace', whiteSpace: 'nowrap' }}>
                      {new Date(publisher.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </Box>
                  </Box>
                  <Box component="td" sx={{ padding: '13px 18px', fontSize: '13px', borderBottom: '1px solid var(--border-light)', verticalAlign: 'middle' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                      <Button
                        onClick={() => handleEdit(publisher)}
                        sx={{
                          width: '30px',
                          height: '30px',
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          border: 'none',
                          background: 'none',
                          transition: 'all 0.15s',
                          '&:hover': {
                            background: 'var(--amber-soft)'
                          }
                        }}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px', color: 'var(--text-muted)' }}>
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                      </Button>
                      <Button
                        onClick={() => handleArchive(publisher)}
                        title="Archive Publisher"
                        sx={{
                          width: '30px',
                          height: '30px',
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          border: 'none',
                          background: 'none',
                          transition: 'all 0.15s',
                          '&:hover': {
                            background: 'var(--amber-soft)'
                          }
                        }}
                      >
                        <ArchiveIconSvg />
                      </Button>
                    </Box>
                  </Box>
                </Box>
              ))
            )}
          </Box>
        </Box>
        
        {/* Pagination */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'flex-end', 
          gap: '14px', 
          padding: '14px 18px', 
          borderTop: '1px solid var(--border-light)' 
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12.5px', color: 'var(--text-secondary)' }}>
            Rows per page: 
            <Select
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setPage(0);
              }}
              sx={{
                border: '1px solid var(--border)',
                background: 'var(--surface)',
                borderRadius: '6px',
                padding: '3px 6px',
                fontSize: '12px',
                outline: 'none',
                '& .MuiSelect-select': {
                  padding: 0,
                  background: 'none',
                  border: 'none'
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                },
              }}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </Box>
          <Box sx={{ fontSize: '12.5px', color: 'var(--text-muted)' }}>
            {page * rowsPerPage + 1}–{Math.min((page + 1) * rowsPerPage, filteredPublishers.length)} of {filteredPublishers.length}
          </Box>
          <Box sx={{ display: 'flex', gap: '4px' }}>
            <Button
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              sx={{
                width: '28px',
                height: '28px',
                borderRadius: '6px',
                border: '1px solid var(--border)',
                background: 'var(--surface)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.15s',
                '&:hover:not(:disabled)': {
                  background: 'var(--bg)'
                }
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '13px', height: '13px', color: 'var(--text-secondary)' }}>
                <polyline points="15 18 9 12 15 6"/>
              </svg>
            </Button>
            <Button
              onClick={() => setPage(page + 1)}
              disabled={(page + 1) * rowsPerPage >= filteredPublishers.length}
              sx={{
                width: '28px',
                height: '28px',
                borderRadius: '6px',
                border: '1px solid var(--border)',
                background: 'var(--surface)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.15s',
                '&:hover:not(:disabled)': {
                  background: 'var(--bg)'
                }
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '13px', height: '13px', color: 'var(--text-secondary)' }}>
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Add Publisher Modal */}
      <Dialog 
        open={formOpen} 
        onClose={handleCloseForm} 
        maxWidth="sm" 
        fullWidth
        BackdropProps={{
          style: {
            backdropFilter: 'blur(8px)',
            backgroundColor: 'rgba(0, 0, 0, 0.5)'
          }
        }}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 16px 48px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)',
            border: '1px solid var(--border)',
            overflow: 'hidden',
            animation: 'modalSlideIn 0.3s ease-out',
            '@keyframes modalSlideIn': {
              from: {
                opacity: 0,
                transform: 'scale(0.9)'
              },
              to: {
                opacity: 1,
                transform: 'scale(1)'
              }
            }
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'flex-start', 
          justifyContent: 'space-between',
          padding: '22px 26px 18px',
          borderBottom: '1px solid var(--border-light)'
        }}>
          <Box>
            <Typography sx={{ fontSize: '16px', fontWeight: 600, letterSpacing: '-0.3px' }}>
              {selectedPublisher ? 'Edit Publisher' : 'Add New Publisher'}
            </Typography>
            <Typography sx={{ fontSize: '12.5px', color: 'var(--text-muted)', mt: 0.5 }}>
              {selectedPublisher ? 'Update publisher information' : 'Add an outreach contact or guest post publisher'}
            </Typography>
          </Box>
          <Button
            onClick={handleCloseForm}
            sx={{
              width: '30px',
              height: '30px',
              borderRadius: '6px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 0.15s',
              flexShrink: 0,
              '&:hover': {
                background: 'var(--bg)'
              }
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '16px', height: '16px', color: 'var(--text-muted)' }}>
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </Button>
        </DialogTitle>
        
        <DialogContent sx={{ padding: '22px 26px', display: 'flex', flexDirection: 'column', gap: '18px' }}>
          {deleteError && (
            <Alert severity="error" onClose={() => setDeleteError('')}>
              {deleteError}
            </Alert>
          )}
          
          <Typography sx={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.7px' }}>
            Publisher Information
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <Typography sx={{ fontSize: '12px', fontWeight: 500, color: 'var(--text-secondary)' }}>
                Publisher Name <Box component="span" sx={{ color: 'var(--red)', ml: '2px' }}>*</Box>
              </Typography>
              <input
                className="form-input"
                type="text"
                value={formData.publisherName}
                onChange={(e) => setFormData({ ...formData, publisherName: e.target.value })}
                placeholder="e.g. teche minds"
                style={{
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '9px 12px',
                  fontSize: '13px',
                  color: 'var(--text-primary)',
                  background: 'var(--surface)',
                  outline: 'none',
                  transition: 'all 0.15s',
                  fontFamily: 'inherit',
                  width: '100%'
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <Typography sx={{ fontSize: '12px', fontWeight: 500, color: 'var(--text-secondary)' }}>
                Email <Box component="span" sx={{ color: 'var(--red)', ml: '2px' }}>*</Box>
              </Typography>
              <input
                className="form-input"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="publisher@domain.com"
                style={{
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '9px 12px',
                  fontSize: '13px',
                  color: 'var(--text-primary)',
                  background: 'var(--surface)',
                  outline: 'none',
                  transition: 'all 0.15s',
                  fontFamily: 'inherit',
                  width: '100%'
                }}
              />
              <Typography sx={{ fontSize: '11.5px', color: 'var(--text-muted)' }}>
                Required — enter a valid email address
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <Typography sx={{ fontSize: '12px', fontWeight: 500, color: 'var(--text-secondary)' }}>
                WhatsApp
              </Typography>
              <input
                className="form-input"
                type="tel"
                value={formData.whatsapp}
                onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                placeholder="+1234567890"
                style={{
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '9px 12px',
                  fontSize: '13px',
                  color: 'var(--text-primary)',
                  background: 'var(--surface)',
                  outline: 'none',
                  transition: 'all 0.15s',
                  fontFamily: 'inherit',
                  width: '100%'
                }}
              />
              <Typography sx={{ fontSize: '11.5px', color: 'var(--text-muted)' }}>
                Optional — numbers only (e.g. +1234567890)
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <Typography sx={{ fontSize: '12px', fontWeight: 500, color: 'var(--text-secondary)' }}>
                Mode of Communication <Box component="span" sx={{ color: 'var(--red)', ml: '2px' }}>*</Box>
              </Typography>
              <select
                value={formData.modeOfCommunication}
                onChange={(e) => setFormData({ ...formData, modeOfCommunication: e.target.value as 'EMAIL' | 'WHATSAPP' })}
                style={{
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '9px 12px',
                  fontSize: '13px',
                  color: 'var(--text-primary)',
                  background: 'var(--surface)',
                  outline: 'none',
                  transition: 'all 0.15s',
                  fontFamily: 'inherit',
                  width: '100%',
                  cursor: 'pointer',
                  backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%239298a8\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Cpolyline points=\'6 9 12 15 18 9\'/%3E%3C/svg%3E")',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 10px center',
                  paddingRight: '32px',
                  appearance: 'none'
                }}
              >
                <option value="EMAIL">Email</option>
                <option value="WHATSAPP">WhatsApp</option>
              </select>
            </Box>
          </Box>
          
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            padding: '12px 14px', 
            background: 'var(--bg)', 
            border: '1px solid var(--border)', 
            borderRadius: 'var(--radius-sm)' 
          }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)' }}>
                Publisher Status
              </Typography>
              <Typography sx={{ fontSize: '11.5px', color: 'var(--text-muted)' }}>
                Toggle to temporarily pause this publisher. Use Archive to remove from this list entirely.
              </Typography>
            </Box>
            <label className="toggle">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
              <span className="toggle-slider"></span>
            </label>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'flex-end', 
          gap: '10px', 
          padding: '16px 26px', 
          borderTop: '1px solid var(--border-light)' 
        }}>
          <Button 
            onClick={handleCloseForm}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              borderRadius: 'var(--radius-sm)',
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer',
              border: '1px solid var(--border)',
              transition: 'all 0.15s',
              whiteSpace: 'nowrap',
              background: 'var(--surface)',
              color: 'var(--text-secondary)',
              '&:hover': {
                background: 'var(--bg)',
                color: 'var(--text-primary)'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={createMutation.isPending || updateMutation.isPending}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              borderRadius: 'var(--radius-sm)',
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer',
              border: 'none',
              transition: 'all 0.15s',
              whiteSpace: 'nowrap',
              background: 'var(--accent)',
              color: 'white',
              boxShadow: '0 1px 2px rgba(37,99,235,0.2)',
              '&:hover': {
                background: 'var(--accent-hover)',
                boxShadow: 'var(--shadow-md)'
              }
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px' }}>
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            {selectedPublisher ? 'Update Publisher' : 'Create Publisher'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ open: false, message: '' })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ open: false, message: '' })} severity="success" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Bulk Upload Modal */}
      <PublisherBulkUploadModal
        isOpen={bulkUploadOpen}
        onClose={() => setBulkUploadOpen(false)}
        onSuccess={(message) => {
          queryClient.invalidateQueries({ queryKey: ['publishers'] });
          setBulkUploadOpen(false);
          setSnackbar({
            open: true,
            message: message || 'Bulk upload completed successfully!',
          });
        }}
      />
    </Box>
  );
};

export default PublishersPage;

// Add CSS styles for form elements
const style = document.createElement('style');
style.textContent = `
  .form-input {
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    padding: 9px 12px;
    font-size: 13px;
    color: var(--text-primary);
    background: var(--surface);
    outline: none;
    transition: all 0.15s;
    font-family: inherit;
    width: 100%;
  }
  .form-input:hover {
    background: var(--bg);
  }
  .form-input:focus {
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(37,99,235,0.08);
  }
  
  .form-select-inp {
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    padding: 9px 12px;
    font-size: 13px;
    color: var(--text-primary);
    background: var(--surface);
    outline: none;
    transition: all 0.15s;
    font-family: inherit;
    width: 100%;
    cursor: pointer;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%239298a8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 10px center;
    padding-right: 32px;
    appearance: none;
  }
  .form-select-inp:hover {
    background: var(--bg);
  }
  .form-select-inp:focus {
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(37,99,235,0.08);
  }
  
  .toggle {
    position: relative;
    display: inline-block;
    width: 40px;
    height: 22px;
  }
  
  .toggle input {
    opacity: 0;
    width: 0;
    height: 0;
  }
  
  .toggle-slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: var(--border);
    transition: 0.2s;
    border-radius: 22px;
  }
  
  .toggle-slider:before {
    position: absolute;
    content: "";
    height: 16px;
    width: 16px;
    left: 3px;
    bottom: 3px;
    background-color: white;
    transition: 0.2s;
    border-radius: 50%;
    box-shadow: 0 1px 3px rgba(0,0,0,0.15);
  }
  
  .toggle input:checked + .toggle-slider {
    background-color: var(--accent);
  }
  
  .toggle input:checked + .toggle-slider:before {
    transform: translateX(18px);
  }
`;
if (!document.querySelector('style[data-publishers-page]')) {
  style.setAttribute('data-publishers-page', 'true');
  document.head.appendChild(style);
}
